import { Link } from "@/lib/nav";
import { Fragment, ReactNode } from "react";

import { ListCard } from "@/components/responsive-list";
import { cn } from "@/lib/utils";

interface Props {
  // 카드 전체가 이 링크 — 없으면 읽기 전용 줄.
  href?: string;
  title: ReactNode;
  // 이름 옆 꼬리표(가견적 등).
  tag?: ReactNode;
  badge?: ReactNode;
  // 둘째 줄 — 최대 두 줄, 넘치면 말줄임.
  subtitle?: ReactNode;
  // 셋째 줄 작은 글자 — 빈 값은 빼고 「 · 」 로 잇는다.
  meta?: Array<string | null | undefined | false>;
  amount?: ReactNode;
  // 메타가 넘치면 말줄임 대신 다음 줄로 꺾는다 — 항목이 많아 끝이 잘리던 목록(고객).
  metaWrap?: boolean;
  // card = 따로 선 카드 · row = 카드 안 구분선 목록의 한 줄.
  variant?: "card" | "row";
  testId?: string;
}

// 폰(md 미만) 목록의 한 행 — 1줄 이름·상태 · 2줄 공사명 · 3줄 메타·금액(시안 b-ListQuotes).
const ListRowCard = ({ href, title, tag, badge, subtitle, meta, amount, metaWrap = false, variant = "card", testId }: Props) => {
  const metaItems = (meta ?? []).filter((item): item is string => Boolean(item));
  const body = (
    <>
      <div className="flex min-w-0 items-center justify-between gap-x-3">
        <span className="flex min-w-0 items-center gap-x-1.5">
          {href ? (
            <Link className="min-w-0 truncate font-semibold after:absolute after:inset-0" href={href}>
              {title}
            </Link>
          ) : (
            <span className="min-w-0 truncate font-semibold">{title}</span>
          )}
          {tag && <span className="shrink-0">{tag}</span>}
        </span>
        {badge && <span className="shrink-0">{badge}</span>}
      </div>
      {subtitle && <p className="line-clamp-2 break-words break-keep">{subtitle}</p>}
      {(metaItems.length > 0 || amount) && (
        <div className={cn("flex min-w-0", metaWrap ? "items-end" : "items-baseline", "justify-between gap-x-3")}>
          <span
            className={cn("min-w-0", metaWrap ? "break-keep" : "truncate", "text-xs tabular-nums text-muted-foreground")}>
            {/* 항목마다 따로 둔다 — 번호가 뒤 날짜와 한 덩이 글자로 붙지 않게. */}
            {metaItems.map((item, index) => (
              <Fragment key={index}>
                {index > 0 && " · "}
                <span className={metaWrap ? "whitespace-nowrap" : undefined}>{item}</span>
              </Fragment>
            ))}
          </span>
          {amount && <span className="shrink-0 whitespace-nowrap font-semibold tabular-nums">{amount}</span>}
        </div>
      )}
    </>
  );

  if (variant === "row") {
    return (
      <li className="relative flex min-w-0 flex-col gap-y-1.5 py-2.5 text-sm" data-testid={testId}>
        {body}
      </li>
    );
  }
  return (
    <ListCard className={cn(href && "hover:bg-muted")} testId={testId}>
      {body}
    </ListCard>
  );
};

interface RowsProps {
  label: string;
  children: ReactNode;
  className?: string;
  testId?: string;
}

// row 줄을 담는 구분선 목록 — 이미 테두리가 있는 카드 안에서 쓴다.
export const ListRows = ({ label, children, className, testId }: RowsProps) => (
  <ul aria-label={label} className={cn("flex min-w-0 flex-col divide-y divide-border", className)} data-testid={testId}>
    {children}
  </ul>
);

export default ListRowCard;
